import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useApi } from '@/shared/app-context';
import { useAsync } from '@/shared/hooks/useAsync';
import { useSessionSocket } from '@/shared/ws/useSessionSocket';
import { EmptyState, Kicker, Muted, Skeleton } from '@/shared/ui';
import styles from './note-redirect.module.scss';

/**
 * L'entrée « Noter » de la bottom nav.
 *
 * Elle ne mène pas à un écran fixe : elle propose les rhums de la séance en cours, avec
 * l'état de la note du membre sur chacun. C'est le raccourci qu'on utilise vingt fois
 * pendant une séance, il doit tomber en un geste.
 */
export function NoteRedirect(): ReactNode {
  const api = useApi();
  const { data, loading, error, reload } = useAsync(() => api.sessions.next(), []);

  // Le lancement de la séance arrive par le socket : l'écran se débloque de lui-même, sans
  // que personne n'ait à tirer pour recharger au moment où la régie ouvre.
  useSessionSocket(data?.id ?? null, {
    onTastingOpened: reload,
    onSessionClosed: reload,
  });

  if (loading) return <Skeleton />;
  if (error) return <EmptyState>{error}</EmptyState>;
  if (!data) return <EmptyState>Aucune séance en cours.</EmptyState>;
  if (data.status === 'CLOSED') {
    return <EmptyState>La dernière séance est clôturée : les notes sont figées.</EmptyState>;
  }
  // Rien à proposer tant que la séance n'est pas lancée : l'API refuserait la note, et un
  // écran qui mène à un refus vaut moins qu'un écran qui explique l'attente.
  if (data.status !== 'OPEN') {
    return (
      <EmptyState>
        La séance n’est pas encore lancée. Les notes ouvriront dès que la régie lancera la
        séance.
      </EmptyState>
    );
  }

  return (
    <div className={styles.page}>
      <header>
        <Kicker>Séance n°{data.number}</Kicker>
        <h1>Que notez-vous ?</h1>
      </header>

      <ul className={styles.list}>
        {data.rums.map((slot) => (
          <li key={slot.rum.id}>
            <Link
              className={styles.row}
              to={
                slot.isBlind && !slot.revealedAt
                  ? `/seances/${data.id}/aveugle`
                  : `/rhums/${slot.rum.id}/noter?sessionId=${data.id}`
              }
            >
              <span className={styles.index}>{slot.orderIndex}</span>
              <span className={styles.info}>
                <strong>{slot.rum.name}</strong>
                <Muted>
                  {slot.hasOwnValidatedTasting
                    ? 'Votre note est validée'
                    : slot.isBlind && !slot.revealedAt
                      ? 'Pronostic à l’aveugle'
                      : 'À noter'}
                </Muted>
              </span>
              {slot.hasOwnValidatedTasting ? <span className={styles.done}>✓</span> : null}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
